import React, { useContext, useEffect, useMemo, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useLocation, useNavigate, useSearchParams } from "react-router-dom";
import { getSubscriptions } from "../../actionCreator/commonActionCreator";
import { AuthContext } from "../../context/AuthContext";
import { Button, Switch, Tag, Row, Col } from "antd";
import { formatCurrency } from "../../utils/formatCurrency";
import { postMethod } from "../../services/HttpServices";

const CheckIcon = () => (
  <svg
    xmlns="http://www.w3.org/2000/svg"
    width="30"
    height="30"
    viewBox="0 0 24 24"
    fill="#017737"
    style={{ display: "inline-block", verticalAlign: "middle" }}
  >
    <path d="M20.314 6.486a1 1 0 0 1 0 1.414L9.707 18.507a1 1 0 0 1-1.414 0l-3.985-3.985a1 1 0 1 1 1.414-1.414L9 16.386l9.9-9.9a1 1 0 0 1 1.414 0"></path>
  </svg>
);

const CrossIcon = () => (
  <svg
    xmlns="http://www.w3.org/2000/svg"
    width="30"
    height="30"
    viewBox="0 0 24 24"
    fill="#d32f2f"
    style={{ display: "inline-block", verticalAlign: "middle" }}
  >
    <path d="M18.3 5.71a1 1 0 0 0-1.42 0L12 10.59 7.12 5.7a1 1 0 1 0-1.41 1.42l4.88 4.88-4.88 4.88a1 1 0 0 0 1.41 1.41L12 13.41l4.88 4.88a1 1 0 0 0 1.42-1.41l-4.89-4.88 4.89-4.88a1 1 0 0 0 0-1.42z"></path>
  </svg>
);

const SectionIcon = () => (
  <svg
    xmlns="http://www.w3.org/2000/svg"
    width="20"
    height="20"
    viewBox="0 0 24 24"
    fill="none"
    stroke="black"
    strokeWidth="2"
    strokeLinecap="round"
    strokeLinejoin="round"
    style={{ marginRight: "8px", verticalAlign: "middle" }}
  >
    <path d="M21.801 10A10 10 0 1 1 17 3.335"></path>
    <path d="m9 11 3 3L22 4"></path>
  </svg>
);

const Plans = ({ setStep, isCheckout }) => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const location = useLocation();
  const plans = useSelector((state) => state.CommonReducer.subscriptions);
  const planList = plans || [];
  const [active, setActive] = useState("INR");
  const [billingCycle, setBillingCycle] = useState("monthly");
  const [calculatedPrices, setCalculatedPrices] = useState({});
  const { isAuthenticated } = useContext(AuthContext);
  const maxDiscountPercentage = useMemo(() => {
    if (!plans || plans.length === 0) return 0;
    let maxPercent = 0;
    plans.forEach((pkg) => {
      const planPrice = Number(pkg.planPrice) || 0;
      if (planPrice <= 0) return;

      const standardYearly = planPrice * 12;
      let savings = 0;

      if (
        pkg.yearlyBasePriceOverride &&
        Number(pkg.yearlyBasePriceOverride) > 0
      ) {
        savings = standardYearly - Number(pkg.yearlyBasePriceOverride);
      } else if (
        pkg.approvedYearlyDiscount &&
        Number(pkg.approvedYearlyDiscount) > 0
      ) {
        savings = Number(pkg.approvedYearlyDiscount);
      }

      if (savings > 0) {
        maxPercent = Math.max(maxPercent, (savings / standardYearly) * 100);
      }
    });

    return maxPercent > 0 ? Math.round(maxPercent) : 0;
  }, [plans]);

  const changeCurrency = (curr) => {
    setActive(curr === "INR" ? "INR" : "USD");
  };

  useEffect(() => {
    dispatch(getSubscriptions());
  }, [dispatch]);

  useEffect(() => {
    if (planList.length > 0) {
      const fetchPrices = async () => {
        try {
          const results = await Promise.all(
            planList.map((pkg) =>
              postMethod("/api/public/pricing/calculate", {
                packageId: pkg.id,
                billingCycle: billingCycle.toUpperCase(),
                currency: active,
              }).then((res) => ({ id: pkg.id, ...res.data })),
            ),
          );
          const newPrices = {};
          results.forEach((r) => {
            newPrices[r.id] = r;
          });
          setCalculatedPrices(newPrices);
        } catch (error) {
          console.error("Failed to fetch dynamic prices:", error);
        }
      };
      fetchPrices();
    }
  }, [planList, billingCycle, active]);

  const yearlyDiscountLabel = useMemo(() => {
    if (billingCycle !== "yearly") return "";

    const pricingItems = Object.values(calculatedPrices || {});

    const promoPricing = pricingItems.find(
      (item) =>
        Number(item?.baseCost) > 0 && Number(item?.promoDiscount) > 0,
    );
    if (promoPricing) {
      const baseCost = Number(promoPricing.baseCost);
      const promoDiscount = Number(promoPricing.promoDiscount);
      const percent = Math.round((promoDiscount / baseCost) * 100);

      if (percent > 0) return `${percent}% Off`;
    }

    const yearlyPricing = pricingItems.find(
      (item) =>
        Number(item?.baseCost) > 0 && Number(item?.yearlyDiscount) > 0,
    );
    if (yearlyPricing) {
      const baseCost = Number(yearlyPricing.baseCost);
      const yearlyDiscount = Number(yearlyPricing.yearlyDiscount);
      const percent = Math.round((yearlyDiscount / baseCost) * 100);

      if (percent > 0) return `${percent}% Off`;
    }

    const discountedPricing = pricingItems.find(
      (item) =>
        Number(item?.baseCost) > 0 &&
        Number(item?.preTaxTotal) >= 0 &&
        Number(item?.baseCost) > Number(item?.preTaxTotal),
    );
    if (discountedPricing) {
      const baseCost = Number(discountedPricing.baseCost);
      const preTaxTotal = Number(discountedPricing.preTaxTotal);
      const percent = Math.round(((baseCost - preTaxTotal) / baseCost) * 100);

      if (percent > 0) return `${percent}% Off`;
    }

    return maxDiscountPercentage > 0 ? `Up to ${maxDiscountPercentage}% Off` : "";
  }, [billingCycle, calculatedPrices, maxDiscountPercentage]);

  const getMonthlyPrice = (pkg, usdValue) => {
    if (active === "USD") return usdValue;
    return Number.parseInt(pkg?.planPrice, 10) || 0;
  };

  const getAnnualPrice = (pkg, usdMonthlyBase) => {
    if (active === "USD") return usdMonthlyBase * 12 * 0.8;
    if (!pkg) return 0;

    const override = Number(pkg.yearlyBasePriceOverride);
    if (override > 0) return override;

    const baseCost = (Number(pkg.planPrice) || 0) * 12;
    const discount = Number(pkg.approvedYearlyDiscount) || 0;
    return baseCost - discount;
  };

  const usdMonthlyFallback = [39, 99, 199];
  const getUsdFallback = (pkg) => {
    const idx = planList.findIndex((p) => p?.id === pkg?.id);
    return usdMonthlyFallback[idx] || 0;
  };

  const getPlanPrice = (pkg, usdMonthly) => {
    const dynamic = calculatedPrices[pkg?.id];
    if (dynamic?.preTaxTotal != null) return Number(dynamic.preTaxTotal);
    return billingCycle === "yearly"
      ? getAnnualPrice(pkg, usdMonthly)
      : getMonthlyPrice(pkg, usdMonthly);
  };

  const displayPlans = useMemo(() => {
    return planList.filter((pkg) => {
      const price = getPlanPrice(pkg, getUsdFallback(pkg));
      return Number(price) > 0;
    });
  }, [planList, calculatedPrices, billingCycle, active]);

  const planDisplay = useMemo(() => {
    return displayPlans.map((pkg, index) => ({
      id: pkg?.id,
      label: pkg?.name || `Plan ${index + 1}`,
      pkg,
      usdMonthly: getUsdFallback(pkg),
      highlight: index === 1,
    }));
  }, [displayPlans]);

  const styles = {
    highlightText: {
      fontWeight: "700",
      color: "#1d1e1f",
      fontSize: "18px",
      lineHeight: "32px",
    },

    container: {
      padding: isCheckout ? "20px 0" : "80px 20px",
      fontFamily: "Inter, system-ui, sans-serif",
      maxWidth: "1200px",
      margin: "auto",
    },
    headingWrapper: {
      position: "relative",
      display: "flex",
      justifyContent: "center",
      alignItems: "center",
      marginBottom: "40px",
      flexDirection: "column",
    },
    heading: {
      fontSize: "32px",
      fontWeight: "bold",
      textAlign: "center",
    },
    dropdownWrapper: {
      // position: "absolute",
      right: "0",
    },
    dropdown: {
      padding: "8px 12px",
      fontSize: "14px",
      borderRadius: "6px",
      border: "1px solid #ccc",
      backgroundColor: "#fff",
      appearance: "none",
      backgroundImage:
        "url(\"data:image/svg+xml;utf8,<svg fill='%23333' height='18' viewBox='0 0 24 24' width='18' xmlns='http://www.w3.org/2000/svg'><path d='M7 10l5 5 5-5z'/></svg>\")",
      backgroundRepeat: "no-repeat",
      backgroundPosition: "right 10px center",
      backgroundSize: "16px",
      paddingRight: "30px",
      cursor: "pointer",
      boxShadow: "0px 2px 8px rgba(0,0,0,0.1)",
      marginBottom: "0",
    },
    tableWrapper: {
      overflowX: "auto",
      borderRadius: "10px",
      boxShadow: "0px 8px 24px rgba(0,0,0,0.08)",
      backgroundColor: "#fff",
      border: "1px solid #e0e0e0",
    },
    table: {
      width: "100%",
      borderCollapse: "collapse",
      fontSize: "14px",
      minWidth: isCheckout ? "1000px" : "800px",
    },
    th: {
      padding: "20px 12px",
      backgroundColor: "#ffffff",
      fontWeight: "bold",
      textAlign: "center",
      fontSize: "16px",
      verticalAlign: "middle",
    },
    td: {
      padding: "16px 10px",
      textAlign: "center",
      verticalAlign: "middle",
    },
    sectionHeaderStyle: {
      backgroundColor: "#f5faff",
      fontWeight: "600",
      padding: "16px 18px",
      textAlign: "left",
      fontSize: "15px",
      borderRadius: "12px",
      margin: "12px",
    },
    button: {
      padding: "10px 20px",
      backgroundColor: "#007bff",
      color: "#fff",
      border: "none",
      borderRadius: "6px",
      cursor: "pointer",
      fontSize: "14px",
      fontWeight: "bold",
    },
  };

  const renderCellContent = (value) => {
    if (value === true) return <CheckIcon />;
    if (value === false) return <CrossIcon />;
    return value;
  };

  const hasFeature = (pkg, internalName) =>
    Boolean(pkg?.features?.find((d) => d?.internalName === internalName));

  const mapPlans = (fn) => planDisplay.map((plan, idx) => fn(plan.pkg, idx));

  const renderRow = (feature, values, key) => (
    <tr key={key}>
      <td
        style={{
          ...styles.td,
          textAlign: "left",
          fontWeight: "500",
          backgroundColor: "#fdfdfd",
          paddingLeft: "30px",
        }}
      >
        {feature}
      </td>
      {values.map((value, idx) => (
        <td key={`${key}-${idx}`} style={styles.td}>
          {renderCellContent(value)}
        </td>
      ))}
    </tr>
  );

  const renderSection = (title, key) => (
    <tr key={key}>
      <td colSpan={planDisplay.length + 1} style={styles.sectionHeaderStyle}>
        <div style={{ display: "flex", alignItems: "center" }}>
          <SectionIcon />
          <span>{title}</span>
        </div>
      </td>
    </tr>
  );

  const renderPlanTitle = (name, price, pkgId, highlight) => {
    const isGrowth = highlight;
    const dynamic = calculatedPrices[pkgId];
    const showStrike =
      dynamic && Number(dynamic.baseCost) > Number(dynamic.preTaxTotal);
    const showOffer = Boolean(dynamic?.appliedOfferName);

    return (
      <div>
        <div
          style={{
            ...styles.highlightText,
            // border: isGrowth ? "2px solid green" : styles.highlightText.border,
            // color: isGrowth ? "green" : styles.highlightText.color,
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
            gap: "6px",
            padding: "2px 12px",
          }}
        >
          <span>{name}</span>
          {isGrowth && (
            <span
              style={{
                fontWeight: 500,
                fontSize: "12px",
                // backgroundColor: "#b94b27",
                backgroundColor: "green",
                padding: "0px 6px",
                borderRadius: "32px",
                height: "18px",
                lineHeight: "18px",
                color: "#fff",
              }}
            >
              🔥Popular
            </span>
          )}
        </div>
        <div
          style={{
            fontWeight: "bold",
            color: "#4b5563",
            fontSize: "18px",
            marginTop: "4px",
          }}
        >
          {price}
        </div>
        {showStrike && (
          <div
            style={{
              color: "#6b7280",
              textDecoration: "line-through",
              marginTop: "6px",
              fontSize: "12px",
            }}
          >
            {formatCurrency(Math.round(Number(dynamic.baseCost) || 0), active)}
            {billingCycle === "yearly" ? "/year" : "/month"}
          </div>
        )}
        {showOffer && (
          <div style={{ marginTop: "8px" }}>
            <Tag
              color="green"
              className="fs-12 fw-bold"
              style={{ borderRadius: "15px", padding: "2px 10px" }}
            >
              Offer: {dynamic.appliedOfferName}
            </Tag>
          </div>
        )}
      </div>
    );
  };

  const buildPlanPrice = (pkg, usdMonthly) => {
    const dynamic = calculatedPrices[pkg?.id];
    const fallback =
      billingCycle === "yearly"
        ? getAnnualPrice(pkg, usdMonthly)
        : getMonthlyPrice(pkg, usdMonthly);
    const amount = dynamic?.preTaxTotal ?? fallback;
    const per = billingCycle === "yearly" ? "/year" : "/month";
    return `${formatCurrency(Math.round(Number(amount) || 0), active)}${per}`;
  };

  const handleSubscribe = (planId) => {
    if (isCheckout) {
      setStep(1);
      searchParams.set("plan", String(planId));
      searchParams.set("cycle", billingCycle.toUpperCase());
      searchParams.set("currency", active);
      navigate(`${location.pathname}?${searchParams.toString()}`);
      return;
    }

    navigate(
      isAuthenticated
        ? `/checkout?plan=${planId}&cycle=${billingCycle.toUpperCase()}&currency=${active}`
        : `/signup?type=subscribe&plan=${planId}&cycle=${billingCycle.toUpperCase()}&currency=${active}`,
    );
  };

  return (
    <div style={styles.container}>
      {!isCheckout && (
        <div style={styles.headingWrapper}>
          <h1 className="heading39px roboto-bold mb10 text-color-131415">
            Choose the Best Plan for Your Hospitality Business
          </h1>
          <p className="heading18px roboto-regular mb20 text-color-131415">
            Scalable, transparent pricing — no hidden fees, cancel anytime.
          </p>
          <Row
            align="middle"
            className="tw-flex tw-items-center tw-justify-between tw-w-full tw-mt-4"
          >
            <Col xs={24} md={8}></Col>
            <Col
              xs={24}
              md={8}
              className="tw-flex tw-items-center tw-justify-center"
            >
              <div
                className="rounded-circle bg-muted"
                style={{ width: "fit-content" }}
              >
                <Button
                  className={
                    active === "INR"
                      ? "theme-btn py-20"
                      : "bg-muted bg-muted-hover border-none py-20"
                  }
                  shape="round"
                  style={{ minWidth: 80, maxWidth: 80 }}
                  onClick={() => changeCurrency("INR")}
                >
                  ₹ INR
                </Button>
                <Button
                  className={
                    active === "USD"
                      ? "theme-btn py-20"
                      : "bg-muted bg-muted-hover border-none py-20"
                  }
                  shape="round"
                  style={{ minWidth: 80, maxWidth: 80 }}
                  onClick={() => changeCurrency("USD")}
                >
                  $ USD
                </Button>
              </div>
            </Col>
            <Col
              xs={24}
              md={8}
              className="tw-flex tw-items-center gap-10 tw-justify-center md:tw-justify-end"
            >
              <span className="tw-font-bold">Monthly</span>
              <Switch
                checked={billingCycle === "yearly"}
                onChange={(checked) =>
                  setBillingCycle(checked ? "yearly" : "monthly")
                }
                style={{
                  backgroundColor:
                    billingCycle === "yearly" ? "#5D44FF" : "#D9D9D9",
                  margin: "0 8px",
                }}
              />

              <span className="tw-font-bold">
                Yearly{" "}
                {billingCycle === "yearly" && yearlyDiscountLabel ? (
                  <span style={{ color: "#5D44FF" }}>({yearlyDiscountLabel})</span>
                ) : null}
              </span>
            </Col>
          </Row>
        </div>
      )}

      <div style={styles.tableWrapper}>
        <table style={styles.table}>
          <thead>
            <tr>
              <th style={styles.th}>Feature / Plan</th>
              {planDisplay.map((plan) => (
                <th key={plan.label} style={styles.th}>
                  {renderPlanTitle(
                    plan.label,
                    buildPlanPrice(plan.pkg, plan.usdMonthly),
                    plan.id,
                    plan.highlight,
                  )}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {renderSection("Lead Generation & Capture", "lead")}
            {renderRow(
              "Web-to-Lead Capture",
              mapPlans((pkg) => hasFeature(pkg, "wtl")),
              "web-lead",
            )}

            {renderRow(
              "Custom Forms (Branded Forms)",
              mapPlans((pkg) =>
                hasFeature(pkg, "customforms")
                  ? `${pkg?.customFieldLimit ?? 0} Custom Fields`
                  : false,
              ),
              "custom-forms",
            )}
            {renderRow(
              "Data-to-Lead Conversion",
              mapPlans((pkg) =>
                hasFeature(pkg, "datatolead")
                  ? `${pkg?.leadCreationLimit ?? 0} conversions/month`
                  : false,
              ),
              "data-lead",
            )}
            {renderRow(
              "Integrated Data Sources",
              mapPlans((pkg, idx) => {
                if (!hasFeature(pkg, "datasources")) return false;
                if (idx === 1) return "Basic";
                if (idx >= 2) return "Full Access";
                return true;
              }),
              "data-sources",
            )}
            {renderRow(
              "Kanban Pipeline View",
              mapPlans((pkg, idx) => {
                if (!hasFeature(pkg, "kanban")) return false;
                if (idx === 1) return "Basic Stages";
                if (idx >= 2) return "Customizable + Filters";
                return true;
              }),
              "kanban",
            )}

            {renderSection("Review & Reputation Management", "review")}
            {renderRow(
              "Unified Review Management",
              mapPlans((pkg, idx) => {
                if (!hasFeature(pkg, "unifiedreviews")) return false;
                if (idx === 1) return "Google only";
                if (idx >= 2) return "Google + OTA platforms";
                return true;
              }),
              "review-mgmt",
            )}
            {/* {renderRow(
              "Review Response Templates",
              false,
              false,
              true,
              "response-templates"
            )}
            {renderRow(
              "Sentiment Insights (Upcoming)",
              false,
              false,
              true,
              "sentiment"
            )} */}

            {renderSection("Engagement & Communication", "engagement")}
            {renderRow(
              "Direct Email Integration",
              mapPlans((pkg) => hasFeature(pkg, "emailintegration")),
              // "1 inbox",
              // "Up to 3 inboxes",
              // "Multi-inbox + history",
              "direct-email",
            )}
            {renderRow(
              "Unified Lead Timeline",
              mapPlans((pkg) => hasFeature(pkg, "leadtimeline")),
              "lead-timeline",
            )}
            {/* {renderRow(
              "WhatsApp Integration (Upcoming)",
              false,
              false,
              "Beta Access",
              "whatsapp"
            )}
            {renderRow(
              "Email Sequencing Engine (Upcoming)",
              false,
              "Lite",
              "Full Sequences + Triggers",
              "email-sequence"
            )}

            {renderSection("Automation & Intelligence", "automation")}
            {renderRow(
              "Gen AI Sales Widget (Upcoming)",
              false,
              "Suggestions Only",
              "Email Drafts + Reply Assist",
              "gen-ai"
            )} */}
            {renderRow(
              "Auto Lead Scoring & Tags",
              mapPlans((pkg) => hasFeature(pkg, "leadtags")),
              "lead-scoring",
            )}
            {/* {renderRow(
              "Campaign Automation Rules",
              false,
              true,
              true,
              "campaign-rules"
            )} */}

            {renderSection("Support & Add-ons", "support")}
            {renderRow(
              "Email Support",
              mapPlans(() => true),
              "email-support",
            )}
            {renderRow(
              "Priority Support",
              mapPlans((pkg) => hasFeature(pkg, "prioritysupport")),
              "priority-support",
            )}
            {renderRow(
              "API / Webhook Access",
              mapPlans((pkg) => hasFeature(pkg, "apiwebhook")),
              "api-access",
            )}
            {renderRow(
              "Onboarding Support",
              mapPlans((_, idx) => {
                if (idx === 0) return "Basic";
                if (idx === 1) return "Standard";
                return "Dedicated";
              }),
              "onboarding",
            )}

            <tr>
              <td
                style={{
                  ...styles.td,
                  fontWeight: "bold",
                  textAlign: "left",
                  backgroundColor: "#fdfdfd",
                }}
              >
                &nbsp;
              </td>
              {planDisplay.map((plan) => (
                <td key={`cta-${plan.id}`} style={styles.td}>
                  <Button
                    type="primary"
                    className="theme-btn"
                    onClick={() => {
                      handleSubscribe(plan?.pkg?.id);
                    }}
                  >
                    Subscribe Now
                  </Button>
                </td>
              ))}
            </tr>
          </tbody>
        </table>
      </div>
    </div>
  );
};

export default Plans;
